/**
 * firebase.js
 * Firebase initialization for the Runtime Engine.
 *
 * All config is read from VITE_FIREBASE_* environment variables so that:
 *  - Local dev uses .env.local values pointing at the emulator stack
 *  - Production builds are configured at Vite build time via Cloud Build
 *    --set-env-vars without touching source code
 *
 * Required env vars (see .env.example):
 *   VITE_FIREBASE_API_KEY
 *   VITE_FIREBASE_AUTH_DOMAIN
 *   VITE_FIREBASE_PROJECT_ID
 *   VITE_FIREBASE_STORAGE_BUCKET
 *   VITE_FIREBASE_MESSAGING_SENDER_ID
 *   VITE_FIREBASE_APP_ID
 *
 * Optional:
 *   VITE_FIREBASE_MEASUREMENT_ID   (Analytics — omit to disable)
 *   VITE_APPCHECK_SITE_KEY          (reCAPTCHA Enterprise — required in prod)
 */

import { initializeApp } from 'firebase/app';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';

// ---------------------------------------------------------------------------
// Config — read from Vite env vars, validated at startup
// ---------------------------------------------------------------------------

const required = [
    'VITE_FIREBASE_API_KEY',
    'VITE_FIREBASE_AUTH_DOMAIN',
    'VITE_FIREBASE_PROJECT_ID',
    'VITE_FIREBASE_STORAGE_BUCKET',
    'VITE_FIREBASE_MESSAGING_SENDER_ID',
    'VITE_FIREBASE_APP_ID',
];

const missing = required.filter(key => !import.meta.env[key]);
if (missing.length > 0) {
    throw new Error(
        `[Firebase] Missing required environment variables:\n  ${missing.join('\n  ')}\n` +
        'Copy .env.example to .env.local and fill in the values.'
    );
}

const firebaseConfig = {
    // Current production values for animalmatch-prod are in .env.example as comments.
    // Do NOT hardcode them here — use .env.local for local dev, Cloud Build vars for prod.
    apiKey:            import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId:             import.meta.env.VITE_FIREBASE_APP_ID,
    // measurementId is optional — Analytics is disabled if omitted
    ...(import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
        ? { measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID }
        : {}),
};

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

const app  = initializeApp(firebaseConfig);
const db   = getFirestore(app);
const auth = getAuth(app);

// ---------------------------------------------------------------------------
// App Check (reCAPTCHA Enterprise)
// Disabled on localhost — emulator dev does not require App Check tokens.
// ---------------------------------------------------------------------------

const isLocal = window.location.hostname === 'localhost'
    || window.location.hostname === '127.0.0.1';

let appCheck = null;

if (!isLocal) {
    try {
        const siteKey = import.meta.env.VITE_APPCHECK_SITE_KEY;
        if (!siteKey) {
            console.warn('[Firebase] VITE_APPCHECK_SITE_KEY is not set — App Check will not initialize in production.');
        } else {
            appCheck = initializeAppCheck(app, {
                provider: new ReCaptchaEnterpriseProvider(siteKey),
                isTokenAutoRefreshEnabled: true,
            });
            console.log('[Firebase] ✅ App Check initialized');
        }
    } catch (e) {
        console.error('[Firebase] ❌ App Check failed to initialize:', e);
    }
} else {
    console.log('[Firebase] 🛡️ App Check disabled for local emulator development');
}

// ---------------------------------------------------------------------------
// Emulator connections (localhost only)
// ---------------------------------------------------------------------------

if (isLocal) {
    console.log('[Firebase] 🔧 Connecting to emulators');
    connectFirestoreEmulator(db, 'localhost', 8081);
    connectAuthEmulator(auth, 'http://localhost:9099');
}

export { app, db, auth, appCheck };
